import React, { useEffect, useState } from "react";
import FacultyListing from "./FacultyListing";
import { columnTypes } from "src/components/UI/atoms/ATMTable/ATMTable";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "src/redux/store";
import {
  setIsOpenAddDialog,
  setIsOpenEditDialog,
  setIsTableLoading,
  setPage,
  setRowsPerPage,
  setSearchValue,
  setItems,
  setTotalItems,
} from "src/redux/slices/FacultySlice";
import ATMMenu from "src/components/UI/atoms/ATMMenu/ATMMenu";
import { showConfirmationDialog } from "src/utils/showConfirmationDialog";
import { showToast } from "src/utils/showToaster";
import AddFacultyWrapper from "../Add/AddFacultyWrapper";
import {
  useChangeStatusFacultyByIdMutation,
  useDeleteFacultyByIdMutation,
  useGetAllFacultyDataQuery,
} from "src/services/FacultyService";
import EditFacultyWrapper from "../Edit/EditFacultyWrapper";
import SideNavLayout from "src/components/layouts/SideNavLayout/SideNavLayout";
import { usePermission } from "src/hooks/usePermission";

type Props = {};

const FacultyListingWrapper = (props: Props) => {
  const dispatch = useDispatch();
  const {
    items,
    totalItems,
    page,
    rowsPerPage,
    isOpenAddDialog,
    isOpenEditDialog,
    searchValue,
    isTableLoading,
  } = useSelector((state: RootState) => state.faculty);

  const [deleteFaculty] = useDeleteFacultyByIdMutation();
  const [changeStatusFaculty] = useChangeStatusFacultyByIdMutation();

  const [selectedFacultyId, setSelectedFacultyId] = useState<string>();
  const { data, isLoading, isFetching } = useGetAllFacultyDataQuery({
    limit: rowsPerPage,
    searchValue: searchValue,
    params: ["facultyName"],
    page: page,
    filterBy: [],
    dateFilter: {},
    orderBy: "createdAt",
    orderByValue: -1,
    isPaginationRequired: true,
  });

  // Setting Module Data
  useEffect(() => {
    if (isLoading || isFetching) {
      dispatch(setIsTableLoading(true));
    } else {
      dispatch(setItems(data?.data || []));
      dispatch(setIsTableLoading(false));
      dispatch(setTotalItems(data?.totalItem || 0));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isLoading, isFetching]);

  const handleDeleteFaculty = (row: string) => {
    showConfirmationDialog({
      title: "Hands Up",
      text: "Are you sure want to delete this Faculty ?",
      icon: "question",
      showCancelButton: true,
      next: (result) => {
        if (result?.isConfirmed) {
          deleteFaculty(row).then((res: any) => {
            if (res.error) {
              showToast("error", res?.error?.data?.message);
            } else {
              showToast("success", res?.data?.message);
            }
          });
        }
      },
    });
  };

  const handleChangeStatusFaculty = (row: string) => {
    showConfirmationDialog({
      title: "Hands Up",
      text: "Are you sure want to change status of this Faculty ?",
      icon: "question",
      showCancelButton: true,
      next: (result) => {
        if (result?.isConfirmed) {
          changeStatusFaculty({ id: row }).then((res: any) => {
            if (res.error) {
              showToast("error", res?.error?.data?.message);
            } else {
              showToast("success", res?.data?.message);
            }
          });
        }
      },
    });
  };

  const moduleName = "FACULTY";

  const canEdit = usePermission(moduleName, "UPDATE");
  const canDelete = usePermission(moduleName, "DELETE");
  const canActive = usePermission(moduleName, "ACTIVE/DEACTIVE");

  const columns: columnTypes[] = [
    { field: "facultyName", headerName: "Faculty Name", flex: "flex-[1_1_0%]" },
    {
      field: "action",
      headerName: "Actions",
      flex: "flex-[1_1_0%]",
      align: "end",
      extraClasses: "",
      renderCell: (row: any) => {
        const options = [];

        if (canEdit) {
          options.push({
            label: "Edit",
            onClick: () => {
              dispatch(setIsOpenEditDialog(true));
              setSelectedFacultyId(row?._id);
            },
          });
        }

        if (canActive) {
          options.push({
            label: "Active/Deactive",
            onClick: () => {
              handleChangeStatusFaculty(row?._id);
            },
          });
        }

        if (canDelete) {
          options.push({
            label: "Delete",
            onClick: () => {
              handleDeleteFaculty(row?._id);
            },
          });
        }

        return <ATMMenu options={options} />;
      },
    },
  ];

  return (
    <SideNavLayout>
      <>
        <div>
          <FacultyListing
            columns={columns}
            rows={items}
            paginationProps={{
              isTableLoading,
              totalItems,
              page,
              rowsPerPage,
              searchValue,
              setPage: (newPage) => dispatch(setPage(newPage)),
              setRowsPerPage: (newRowsPerPage) =>
                dispatch(setRowsPerPage(newRowsPerPage)),
              setIsOpenAddDialog: (value) =>
                dispatch(setIsOpenAddDialog(value)),
              setSearchValue: (newValue) => dispatch(setSearchValue(newValue)),
            }}
          />
        </div>
        {isOpenAddDialog ? (
          <AddFacultyWrapper
            onClose={() => dispatch(setIsOpenAddDialog(false))}
          />
        ) : null}
        {isOpenEditDialog ? (
          <EditFacultyWrapper
            onClose={() => dispatch(setIsOpenEditDialog(false))}
            selectedFacultyId={selectedFacultyId || ""}
          />
        ) : null}
      </>
    </SideNavLayout>
  );
};

export default FacultyListingWrapper;
